(ns common.schema.entities.workspace.kyc.automated-renewal.configuration
  (:require
   [malli.core :as m]
   [clojure.edn :as edn]
   [common.util :as cu]
   [common.localization :as loc]))

(def TimeUnitSchema
  "Valid time units for durations"
  [:enum :minutes :hours :days :weeks])

(def DurationSchema
  "Duration schema with value and unit"
  [:map
   {:closed true}
   [:value [:int {:min 1}]]
   [:unit TimeUnitSchema]])

(def EmailSubjectSchema
  [:map
   {:closed true}
   [:en [:string {:min 1}]]
   [:fi [:string {:optional true}]]])

(def EmailTemplateSchema
  [:map
   {:closed true}
   [:en [:string {:min 1}]]
   [:fi [:string {:optional true}]]])

(def ReminderEmailSchema
  [:map
   {:closed true}
   [:id [:string {:min 1}]]
   [:after DurationSchema]
   [:subject EmailSubjectSchema]
   [:template EmailTemplateSchema]])

(def ReminderConfigSchema
  [:map
   [:reminders
    ;; 2 reminders hard coded
    [:vector {:min 2 :max 2} ReminderEmailSchema]]])

(def RenewalConfigSchema
  [:map
   {:closed? true}
   [:version [:enum 1 2]]
   [:reminder-config ReminderConfigSchema]])

(defn duration->milliseconds
  "Convert a duration to milliseconds"
  [{:keys [value unit]}]
  (case unit
    :minutes (* value 60 1000)
    :hours (* value 60 60 1000)
    :days (* value 24 60 60 1000)
    :weeks (* value 7 24 60 60 1000)))

(defn duration-has-passed?
  "Check if the specified duration has passed since a reference timestamp.
   Returns true if enough time has elapsed.

   Args:
   - current-ts: Current timestamp in milliseconds
   - from-ts: Reference timestamp in milliseconds to measure duration from
   - duration: Duration map with :value and :unit keys"
  [{:keys [current-ts from-ts duration]}]
  (let [interval-ms (duration->milliseconds duration)
        elapsed-ms (- current-ts from-ts)]
    (>= elapsed-ms interval-ms)))

;; TODO: We should think how to make the email templates
;; for different tenants in a more scalable way
(def tenant-email-templates
  {"CapMan" ["capman-kyc-reminder-email-en"
             "capman-kyc-reminder-email-fi"
             "capman-kyc-first-reminder-email-en.html"
             "capman-kyc-first-reminder-email-fi.html"
             "capman-kyc-second-reminder-email-en.html"
             "capman-kyc-second-reminder-email-fi.html"]})

(defn valid-email-template-filename?
  [tenant template-filename]
  (let [tenant-name (:tenant/name tenant)
        templates (get tenant-email-templates tenant-name [])]
    (some #{template-filename} templates)))

(defn valid-configuration?
  [tenant configuration]
  (println "CONFIGURATION : " tenant " > " configuration)
  (let [valid-schema? (m/validate RenewalConfigSchema configuration)
        valid-templates? (->> configuration
                              :reminder-config
                              :reminders
                              (mapcat (fn [reminder-config]
                                        (->> reminder-config
                                             :template
                                             vals)))
                              (every? (partial valid-email-template-filename? tenant)))]
    (and valid-schema?
         valid-templates?)))

(def capman-default-configuration-v1
  {:version 1
   :reminder-config
   {:enabled? false
    :reminders [{:id       "first-reminder"
                 :after    {:value 2
                            :unit :weeks}
                 :subject  {:en "Reminder: CapMan | Customer information request (KYC)"
                            :fi "Muistutus: CapMan | Asiakastietokysely (KYC)"}
                 :template {:en "capman-kyc-reminder-email-en"
                            :fi "capman-kyc-reminder-email-fi"}}
                {:id       "second-reminder"
                 :after    {:value 2
                            :unit :weeks}
                 :subject  {:en "Reminder: CapMan | Customer information request (KYC)"
                            :fi "Muistutus: CapMan | Asiakastietokysely (KYC)"}
                 :template {:en "capman-kyc-reminder-email-en"
                            :fi "capman-kyc-reminder-email-fi"}}]}})

(def capman-default-configuration-v2
  {:version 2
   :reminder-config
   {:enabled? false
    :reminders [{:id       "first-reminder"
                 :after    {:value 2
                            :unit :weeks}
                 :subject  {:en (loc/tr :capman-kyc-reminder-subject)
                            :fi (loc/tr :capman-kyc-reminder-subject nil :fi)}
                 :template {:en "capman-kyc-first-reminder-email-en.html"
                            :fi "capman-kyc-first-reminder-email-fi.html"}}
                {:id       "second-reminder"
                 :after    {:value 2
                            :unit :weeks}
                 :subject  {:en (loc/tr :capman-kyc-reminder-subject)
                            :fi (loc/tr :capman-kyc-reminder-subject nil :fi)}
                 :template {:en "capman-kyc-second-reminder-email-en.html"
                            :fi "capman-kyc-second-reminder-email-fi.html"}}]}})

(def capman-default-configuration capman-default-configuration-v2)

(defn merge-config-with-hard-coded-email-subject-and-template
  [tenant config]
  (case (:tenant/name tenant)
    "CapMan"
    (let [reminders-path [:reminder-config :reminders]
          curr-reminders (get-in config reminders-path)
          desired-reminders (get-in capman-default-configuration reminders-path)
          new-reminders (->> curr-reminders
                             (mapv (fn [curr]
                                     (let [{:keys [id]} curr
                                           desired (cu/find-one :id id desired-reminders)]
                                       (if desired
                                         (assoc curr
                                                :subject (:subject desired)
                                                :template (:template desired))
                                         curr)))))
          new-config (assoc-in config reminders-path new-reminders)]
      new-config)

    config))

(defn get-tenant-default-configuration
  [tenant]
  (case (:tenant/name tenant)
    "CapMan" capman-default-configuration
    ;; TODO: Add some better way to add tenant configurations
    {:version 1}))

(defn get-configuration
  [tenant]
  (let [config (:tenant/automated-kyc-renewal-configuration tenant)
        config (if (string? config)
                 (edn/read-string config)
                 config)]
    (when config
      (merge-config-with-hard-coded-email-subject-and-template tenant config))))

(defn reminders-enabled-for-tenant?
  [tenant]
  (some-> tenant
          get-configuration
          (get-in [:reminder-config :enabled?])))

(defn get-nth-reminder-config
  "zero-based indexing"
  [tenant reminder-index]
  (some-> tenant
          get-configuration
          (get-in [:reminder-config :reminders reminder-index])
          (assoc :index reminder-index)))
